import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Building2, Users, Truck, ClipboardList, Bell, Pause, Play, Trash2, KeyRound, X, Plus, Upload } from "lucide-react";
import { fmtDateTime, ROLE_LABEL } from "@/lib/format";
import { ImportModal } from "./Companies";
import { Field } from "./Fleet";
import { validateRut, formatRut } from "@/lib/rut";

const TYPE_LABEL = { rentacar: "Rent-a-Car", workshop: "Taller", client: "Cliente" };

export default function Admin() {
  const [tab, setTab] = useState("stats");
  return (
    <div data-testid="admin-page">
      <PageHeader
        tag="SuperAdmin"
        title="Panel Global"
        description="Métricas, empresas y usuarios de toda la plataforma VETA CAR."
      />
      <div className="border-b border-gray-200 flex gap-1 mb-8 overflow-x-auto">
        {[
          { k: "stats", label: "Estadísticas" },
          { k: "companies", label: "Empresas" },
          { k: "users", label: "Usuarios" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`admin-tab-${t.k}`}
            className={`px-4 md:px-6 py-3 text-xs font-bold uppercase tracking-wider border-b-4 transition-colors ${
              tab === t.k ? "border-blue-600 text-black" : "border-transparent text-gray-500 hover:text-black"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "stats" && <StatsView />}
      {tab === "companies" && <CompaniesView />}
      {tab === "users" && <UsersView />}
    </div>
  );
}

function StatsView() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <p className="text-gray-500">Cargando...</p>;
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
        <Tile icon={Building2} label="Empresas" value={stats.companies.total} testId="stat-companies" />
        <Tile icon={Users} label="Usuarios" value={stats.users} testId="stat-users" />
        <Tile icon={Truck} label="Vehículos" value={stats.vehicles} testId="stat-vehicles" />
        <Tile icon={ClipboardList} label="Órdenes" value={stats.service_orders.total} sub={`${stats.service_orders.pending} pendientes`} testId="stat-orders" />
      </div>
      <div className="grid md:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
        <SmallTile label="Rent-a-Car" value={stats.companies.rentacar} />
        <SmallTile label="Talleres" value={stats.companies.workshop} />
        <SmallTile label="Clientes" value={stats.companies.client} />
        <SmallTile label="Empresas suspendidas" value={stats.companies.suspended} accent={stats.companies.suspended > 0 ? "danger" : ""} />
        <SmallTile label="Contratos activos" value={stats.leases_active} />
        <SmallTile label="Alertas enviadas" value={stats.alerts_sent} />
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="bg-white p-6" data-testid={testId}>
      <div className="flex justify-between">
        <div>
          <p className="veta-tag">{label}</p>
          <p className="font-display text-4xl tracking-tighter mt-2">{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
        {Icon && <Icon size={24} strokeWidth={2.5} className="text-black" />}
      </div>
    </div>
  );
}

function SmallTile({ label, value, accent }) {
  const bg = accent === "danger" ? "bg-red-50" : "bg-white";
  return (
    <div className={`${bg} p-5`}>
      <p className="veta-tag">{label}</p>
      <p className="font-display text-2xl mt-1">{value}</p>
    </div>
  );
}

function CompaniesView() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const load = () => api.get("/admin/companies", { params: type ? { type } : {} }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [type]);

  const toggleSuspend = async (c) => {
    await api.put(`/admin/companies/${c.id}`, { suspended: !c.suspended });
    load();
  };
  const remove = async (c) => {
    await api.delete(`/admin/companies/${c.id}`);
    setConfirm(null);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          <label className="veta-tag">Filtrar por tipo</label>
          <select value={type} onChange={(e) => setType(e.target.value)} className="h-10 px-3 border border-gray-300" data-testid="admin-company-type-filter">
            <option value="">Todas</option>
            <option value="rentacar">Rent-a-Car</option>
            <option value="workshop">Taller</option>
            <option value="client">Cliente</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowImport(true)} className="h-10 px-3 border-2 border-black text-xs font-bold uppercase tracking-wider hover:bg-black hover:text-white flex items-center gap-2" data-testid="admin-import-btn">
            <Upload size={14} strokeWidth={2.5} /> Importar Rent-a-Cars
          </button>
          <button onClick={() => setShowCreate(true)} className="h-10 px-3 bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-600 flex items-center gap-2" data-testid="admin-new-company-btn">
            <Plus size={14} strokeWidth={2.5} /> Nueva Empresa
          </button>
        </div>
      </div>
      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <Th>Nombre</Th><Th>Tipo</Th><Th>Usuarios</Th><Th>Vehículos</Th><Th>Estado</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-gray-200" data-testid={`company-row-${c.id}`}>
                <td className="px-4 py-3">
                  <p className="font-bold text-sm">{c.name}</p>
                  <p className="text-xs text-gray-500 font-mono">{c.email || "—"}</p>
                </td>
                <td className="px-4 py-3 text-sm">{TYPE_LABEL[c.type] || c.type}</td>
                <td className="px-4 py-3 text-sm font-mono">{c.users_count}</td>
                <td className="px-4 py-3 text-sm font-mono">{c.vehicles_count}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 ${c.suspended ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>
                    {c.suspended ? "Suspendida" : "Activa"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => toggleSuspend(c)} className="text-xs font-bold uppercase tracking-wider text-blue-600 underline mr-3 inline-flex items-center gap-1" data-testid={`suspend-btn-${c.id}`}>
                    {c.suspended ? <><Play size={12} strokeWidth={2.5} /> Activar</> : <><Pause size={12} strokeWidth={2.5} /> Suspender</>}
                  </button>
                  <button onClick={() => setConfirm(c)} className="text-xs font-bold uppercase tracking-wider text-red-600 underline inline-flex items-center gap-1" data-testid={`delete-btn-${c.id}`}>
                    <Trash2 size={12} strokeWidth={2.5} /> Eliminar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirm && (
        <ConfirmModal
          title={`¿Eliminar empresa "${confirm.name}"?`}
          body="Se eliminarán también todos sus usuarios y, si es Rent-a-Car, sus vehículos y contratos. Esta acción no se puede deshacer."
          onCancel={() => setConfirm(null)}
          onConfirm={() => remove(confirm)}
        />
      )}

      {showCreate && (
        <CreateCompanyModal onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); }} />
      )}
      {showImport && (
        <ImportModal type="rentacars" onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />
      )}
    </div>
  );
}

function CreateCompanyModal({ onClose, onDone }) {
  const [form, setForm] = useState({
    company_type: "rentacar",
    company_name: "",
    company_rut: "",
    company_email: "",
    company_phone: "",
    company_address: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.company_rut && !validateRut(form.company_rut)) {
      setError(`RUT inválido: ${form.company_rut}`);
      return;
    }
    if (form.admin_email && (!form.admin_name || !form.admin_password || form.admin_password.length < 6)) {
      setError("Si ingresas correo de admin, también debes ingresar nombre y contraseña (mín. 6 caracteres)");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, company_rut: form.company_rut ? formatRut(form.company_rut) : "" };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/admin/companies", payload);
      onDone();
    } catch (e) { setError(e.response?.data?.detail || "Error"); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center md:p-6">
      <div className="bg-white border-4 border-black w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-company-modal">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <p className="veta-tag">SuperAdmin</p>
            <h2 className="font-display text-2xl">Crear empresa + admin</h2>
          </div>
          <button onClick={onClose}><X size={24} strokeWidth={2.5} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className="veta-tag block mb-2">Tipo de empresa</label>
            <select value={form.company_type} onChange={update("company_type")} className="w-full h-12 px-3 border border-gray-300" data-testid="create-comp-type">
              <option value="rentacar">Rent-a-Car</option>
              <option value="workshop">Taller</option>
              <option value="client">Empresa Cliente</option>
            </select>
          </div>
          <Field label="Nombre / Razón Social" required value={form.company_name} onChange={update("company_name")} testId="create-comp-name" />
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="RUT" value={form.company_rut} onChange={update("company_rut")} mono testId="create-comp-rut" />
            <Field label="Teléfono" value={form.company_phone} onChange={update("company_phone")} mono testId="create-comp-phone" />
            <Field label="Correo empresa" type="email" value={form.company_email} onChange={update("company_email")} mono testId="create-comp-email" />
            <Field label="Dirección" value={form.company_address} onChange={update("company_address")} testId="create-comp-address" />
          </div>

          <div className="border-t border-gray-200 pt-5">
            <p className="veta-tag mb-2">Usuario administrador (opcional)</p>
            <p className="text-xs text-gray-500 mb-3">Si lo dejas en blanco, la empresa quedará sin usuarios hasta que alguien se registre con su correo.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Nombre admin" value={form.admin_name} onChange={update("admin_name")} testId="create-admin-name" />
              <Field label="Correo admin" type="email" value={form.admin_email} onChange={update("admin_email")} mono testId="create-admin-email" />
              <Field label="Contraseña admin" type="password" value={form.admin_password} onChange={update("admin_password")} testId="create-admin-pass" />
            </div>
          </div>

          {error && <div className="status-danger p-3 text-sm">{error}</div>}
          <button type="submit" disabled={loading} className="w-full h-12 bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-600 disabled:opacity-60" data-testid="save-create-company">
            {loading ? "Creando..." : "Crear empresa"}
          </button>
        </form>
      </div>
    </div>
  );
}

function UsersView() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    await api.delete(`/admin/users/${u.id}`);
    setConfirm(null);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <Th>Nombre</Th><Th>Correo</Th><Th>Rol</Th><Th>Empresa</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-gray-200" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 text-sm font-bold">{u.name}</td>
                <td className="px-4 py-3 text-sm font-mono">{u.email}</td>
                <td className="px-4 py-3 text-xs font-bold uppercase tracking-wider">{ROLE_LABEL[u.role]}</td>
                <td className="px-4 py-3 text-sm">{u.company?.name || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(u)} className="text-xs font-bold uppercase tracking-wider text-blue-600 underline mr-3" data-testid={`edit-user-${u.id}`}>
                    Editar
                  </button>
                  <button onClick={() => setConfirm(u)} className="text-xs font-bold uppercase tracking-wider text-red-600 underline" data-testid={`delete-user-${u.id}`}>
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditUserModal user={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />
      )}
      {confirm && (
        <ConfirmModal
          title={`¿Eliminar usuario ${confirm.email}?`}
          body="Esta acción no se puede deshacer."
          onCancel={() => setConfirm(null)}
          onConfirm={() => remove(confirm)}
        />
      )}
    </div>
  );
}

function EditUserModal({ user, onClose, onDone }) {
  const [form, setForm] = useState({ name: user.name, role: user.role, new_password: "" });
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { name: form.name, role: form.role };
      if (form.new_password) payload.new_password = form.new_password;
      await api.put(`/admin/users/${user.id}`, payload);
      onDone();
    } catch (e) {
      setError(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center md:p-6">
      <div className="bg-white border-4 border-black w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-user-modal">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <p className="veta-tag">Editar usuario</p>
            <h2 className="font-display text-2xl font-mono text-black">{user.email}</h2>
          </div>
          <button onClick={onClose}><X size={24} strokeWidth={2.5} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className="veta-tag block mb-2">Nombre</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-12 px-3 border border-gray-300" data-testid="edit-user-name" />
          </div>
          <div>
            <label className="veta-tag block mb-2">Rol</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-12 px-3 border border-gray-300" data-testid="edit-user-role">
              <option value="superadmin">Super Administrador</option>
              <option value="rentacar_admin">Admin Rent-a-Car</option>
              <option value="workshop_admin">Admin Taller</option>
              <option value="client_user">Cliente</option>
            </select>
          </div>
          <div>
            <label className="veta-tag block mb-2 flex items-center gap-2"><KeyRound size={12} strokeWidth={2.5} /> Nueva contraseña (opcional)</label>
            <input type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} placeholder="Dejar en blanco para no cambiar" className="w-full h-12 px-3 border border-gray-300" data-testid="edit-user-password" />
          </div>
          {error && <div className="status-danger p-3 text-sm">{error}</div>}
          <button type="submit" className="w-full h-12 bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-600" data-testid="save-user-btn">
            Guardar
          </button>
        </form>
      </div>
    </div>
  );
}

function ConfirmModal({ title, body, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6">
      <div className="bg-white border-4 border-red-600 w-full max-w-md p-6" data-testid="confirm-modal">
        <h3 className="font-display text-2xl text-red-700">{title}</h3>
        <p className="text-sm text-gray-700 mt-3">{body}</p>
        <div className="mt-6 flex gap-3">
          <button onClick={onCancel} className="flex-1 h-12 border-2 border-gray-300 text-xs font-bold uppercase tracking-wider hover:bg-gray-100" data-testid="confirm-cancel">Cancelar</button>
          <button onClick={onConfirm} className="flex-1 h-12 bg-red-600 text-white text-xs font-bold uppercase tracking-wider hover:bg-red-700" data-testid="confirm-ok">Eliminar</button>
        </div>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">{children}</th>;
}
